import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Phone, MoreVertical, Check, CheckCheck,
  Send, Paperclip, Smile, Github, Mail, ChevronLeft, X, ExternalLink, Play,
} from 'lucide-react';

const REACTION_EMOJIS = ['👍🏻', '🔥', '❤️', '💩'];

const getMaxWidth = (type) => {
  if (type === 'tech') return '82%';
  if (type === 'features' || type === 'stats') return '78%';
  if (type === 'contact') return '76%';
  if (type === 'description') return '85%';
  if (type === 'video') return '62%';
  return '72%';
};

// Helper: make @username mentions into Telegram links
const linkifyText = (text) => {
  if (!text) return text;
  const parts = text.split(/(@[a-zA-Z0-9_]+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    /^@[a-zA-Z0-9_]+$/.test(part) ? (
      <a key={i} href={`https://t.me/${part.slice(1)}`} target="_blank" rel="noopener noreferrer"
         style={{ color: '#5288c1', textDecoration: 'none', fontWeight: 500 }}>
        {part}
      </a>
    ) : part
  );
};

// Helper: display name with "Бот" prefix for non-profile projects
const getBotName = (project) => {
  if (!project) return '';
  if (project.isProfile) return project.name;
  return `Бот "${project.name}"`;
};

// ─── Bubble Components ─────────────────────────────────────────────────────────

const IncomingBubble = ({ msg }) => (
  <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '10px 14px' }}>
    <p style={{ color: '#ffffff', fontSize: '15px', margin: 0, lineHeight: 1.55, wordBreak: 'break-word' }}>
      {linkifyText(msg.text)}
    </p>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '3px' }}>
      <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
    </div>
  </div>
);

const OutgoingBubble = ({ msg }) => (
  <div style={{ background: '#2b5278', borderRadius: '18px 18px 4px 18px', padding: '10px 14px' }}>
    <p style={{ color: '#ffffff', fontSize: '15px', margin: 0, lineHeight: 1.55, wordBreak: 'break-word' }}>
      {msg.text}
    </p>
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '3px', marginTop: '3px' }}>
      <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>{msg.time}</span>
      <CheckCheck size={14} color="rgba(255,255,255,0.45)" />
    </div>
  </div>
);

const FeaturesBubble = ({ msg }) => (
  <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '12px 16px' }}>
    <p style={{ color: '#5288c1', fontWeight: 600, fontSize: '12px', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      Возможности
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {msg.items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{
            width: '18px', height: '18px', borderRadius: '50%',
            background: 'rgba(82,136,193,0.15)', border: '1.5px solid #5288c1',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: '1px',
          }}>
            <Check size={10} color="#5288c1" />
          </div>
          <span style={{ color: '#d4e3f0', fontSize: '14px', lineHeight: 1.45 }}>{item}</span>
        </div>
      ))}
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
      <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
    </div>
  </div>
);

const StatsBubble = ({ msg }) => (
  <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '12px 16px', minWidth: '260px' }}>
    <p style={{ color: '#5288c1', fontWeight: 600, fontSize: '12px', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      Статистика
    </p>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {msg.data.map((stat, i) => (
        <div key={i} style={{
          background: 'rgba(82,136,193,0.07)', border: '1px solid rgba(82,136,193,0.18)',
          borderRadius: '10px', padding: '10px 12px', textAlign: 'center',
        }}>
          <div style={{ color: '#5288c1', fontSize: '19px', fontWeight: 700, lineHeight: 1 }}>{stat.value}</div>
          <div style={{ color: '#708499', fontSize: '11px', marginTop: '4px' }}>{stat.label}</div>
        </div>
      ))}
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
      <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
    </div>
  </div>
);

const TechBubble = ({ msg }) => (
  <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '12px 16px' }}>
    <p style={{ color: '#5288c1', fontWeight: 600, fontSize: '12px', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {msg.title || 'Технологии'}
    </p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {msg.items.map((tech, i) => (
        <span key={i} style={{
          background: 'rgba(43,82,120,0.55)', color: '#a8c8e8',
          padding: '4px 11px', borderRadius: '12px', fontSize: '13px',
          fontWeight: 500, border: '1px solid rgba(82,136,193,0.22)',
        }}>
          {tech}
        </span>
      ))}
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
      <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
    </div>
  </div>
);

const ContactBubble = ({ msg }) => {
  const icons = { send: Send, github: Github, mail: Mail };
  return (
    <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '12px 16px', minWidth: '220px' }}>
      <p style={{ color: '#5288c1', fontWeight: 600, fontSize: '12px', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        Контакты
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {msg.items.map((contact, i) => {
          const IconComp = icons[contact.icon] || Mail;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%',
                background: 'rgba(82,136,193,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <IconComp size={16} color="#5288c1" />
              </div>
              <div>
                <div style={{ color: '#708499', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{contact.label}</div>
                {/^@/.test(contact.value) ? (
                  <a href={`https://t.me/${contact.value.slice(1)}`} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#5288c1', fontSize: '14px', fontWeight: 500, marginTop: '1px', display: 'block', textDecoration: 'none' }}>
                    {contact.value}
                  </a>
                ) : (
                  <div style={{ color: '#5288c1', fontSize: '14px', fontWeight: 500, marginTop: '1px' }}>{contact.value}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
        <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
      </div>
    </div>
  );
};

const DescriptionBubble = ({ msg }) => {
  const lines = msg.text.split('\n');
  return (
    <div style={{ background: '#182533', borderRadius: '18px 18px 18px 4px', padding: '12px 16px' }}>
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} style={{ height: '6px' }} />;
        const isBullet = line.trim().startsWith('•');
        const numMatch = line.trim().match(/^(\d+)\.\s+([\s\S]+)/);
        const isSectionHeader = line.trim().endsWith(':') && line.trim().length < 60 && !isBullet;

        if (isBullet) {
          const text = line.trim().replace(/^•\s*/, '');
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#5288c1', flexShrink: 0, marginTop: '7px' }} />
              <span style={{ color: '#d4e3f0', fontSize: '14px', lineHeight: 1.5, wordBreak: 'break-word' }}>{text}</span>
            </div>
          );
        }
        if (numMatch) {
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
              <span style={{ color: '#5288c1', fontSize: '13px', fontWeight: 700, flexShrink: 0, minWidth: '18px', marginTop: '1px' }}>{numMatch[1]}.</span>
              <span style={{ color: '#d4e3f0', fontSize: '14px', lineHeight: 1.5, wordBreak: 'break-word' }}>{numMatch[2]}</span>
            </div>
          );
        }
        return (
          <p key={i} style={{
            color: isSectionHeader ? '#5288c1' : '#ffffff',
            fontSize: '14px',
            fontWeight: isSectionHeader ? 600 : 400,
            lineHeight: 1.55,
            margin: `0 0 ${isSectionHeader ? '8px' : '5px'} 0`,
            wordBreak: 'break-word',
          }}>
            {line}
          </p>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
      </div>
    </div>
  );
};

const VideoBubble = ({ msg }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen]);

  return (
    <>
      {/* Fullscreen video modal */}
      {modalOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setModalOpen(false)}
        >
          <button
            onClick={() => setModalOpen(false)}
            style={{ position: 'absolute', top: '18px', right: '18px', background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2001 }}
          >
            <X size={20} color="#fff" />
          </button>
          <video
            controls
            autoPlay
            style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: '10px', boxShadow: '0 24px 80px rgba(0,0,0,0.8)' }}
            onClick={e => e.stopPropagation()}
          >
            <source src={msg.url} type="video/mp4" />
          </video>
        </div>
      )}

      {/* Thumbnail card */}
      <div
        onClick={() => setModalOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          cursor: 'pointer',
          background: hovered ? 'linear-gradient(135deg, #1f3347 0%, #152130 100%)' : 'linear-gradient(135deg, #182533 0%, #0e1621 100%)',
          borderRadius: '14px',
          overflow: 'hidden',
          border: `1px solid ${hovered ? 'rgba(82,136,193,0.45)' : 'rgba(82,136,193,0.2)'}`,
          position: 'relative',
          minHeight: '130px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '28px 48px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: hovered ? '#5288c1' : 'rgba(82,136,193,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s, transform 0.2s',
            transform: hovered ? 'scale(1.12)' : 'scale(1)',
          }}>
            <Play size={22} color="#fff" style={{ marginLeft: '3px' }} />
          </div>
          <span style={{ color: hovered ? '#a8c8e8' : '#708499', fontSize: '13px', fontWeight: 500, transition: 'color 0.2s' }}>
            Демо-видео
          </span>
        </div>
        <div style={{ position: 'absolute', bottom: '8px', right: '12px' }}>
          <span style={{ color: '#708499', fontSize: '11px' }}>{msg.time}</span>
        </div>
      </div>
    </>
  );
};

const SystemMsg = ({ msg }) => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
    <span style={{
      background: 'rgba(0,0,0,0.35)', color: '#9db5ca',
      fontSize: '12px', padding: '5px 14px', borderRadius: '14px', fontWeight: 500,
    }}>
      {msg.text}
    </span>
  </div>
);

const getBubble = (msg) => {
  switch (msg.type) {
    case 'incoming': return <IncomingBubble msg={msg} />;
    case 'outgoing': return <OutgoingBubble msg={msg} />;
    case 'features': return <FeaturesBubble msg={msg} />;
    case 'stats': return <StatsBubble msg={msg} />;
    case 'tech': return <TechBubble msg={msg} />;
    case 'contact': return <ContactBubble msg={msg} />;
    case 'description': return <DescriptionBubble msg={msg} />;
    case 'video': return <VideoBubble msg={msg} />;
    default: return null;
  }
};

// ─── Reaction Wrapper ──────────────────────────────────────────────────────────

const ReactionWrapper = ({ msg, projectId, reactions, onReact }) => {
  const [hovered, setHovered] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const btnRef = useRef(null);

  // All hooks BEFORE any early returns (React rules)
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  if (msg.type === 'system') return <SystemMsg msg={msg} />;

  const msgKey = `${projectId}_${msg.id}`;
  const msgReactions = reactions[msgKey] || {};
  const hasReactions = Object.keys(msgReactions).length > 0;
  const isOutgoing = msg.type === 'outgoing';
  const maxW = getMaxWidth(msg.type);

  return (
    <div
      style={{ position: 'relative', marginBottom: '6px' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Message row: bubble + reaction button */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '6px',
        flexDirection: isOutgoing ? 'row-reverse' : 'row',
      }}>
        <div style={{ flex: '0 1 auto', maxWidth: maxW, minWidth: 0 }}>
          {getBubble(msg)}
        </div>

        {/* Reaction button + picker positioned BELOW the button */}
        {(hovered || pickerOpen) && (
          <div style={{ position: 'relative', flexShrink: 0, marginBottom: '4px', alignSelf: 'flex-end' }}>
            <button
              ref={btnRef}
              onClick={e => { e.stopPropagation(); setPickerOpen(prev => !prev); }}
              style={{
                width: '26px', height: '26px',
                borderRadius: '50%',
                background: pickerOpen ? '#2b5278' : '#212d3b',
                border: '1px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#708499', fontSize: '18px', lineHeight: 1, fontWeight: 300,
                transition: 'background 0.15s ease',
              }}
            >
              +
            </button>

            {/* Picker below the button */}
            {pickerOpen && (
              <div
                ref={pickerRef}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  [isOutgoing ? 'right' : 'left']: '0',
                  background: '#1e2d3d',
                  borderRadius: '24px',
                  padding: '8px 10px',
                  display: 'flex',
                  gap: '2px',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  zIndex: 30,
                  whiteSpace: 'nowrap',
                }}
                onClick={e => e.stopPropagation()}
              >
                {REACTION_EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => { onReact(msgKey, emoji); setPickerOpen(false); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '22px', padding: '2px 4px', lineHeight: 1,
                      borderRadius: '8px', transition: 'transform 0.1s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.35)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reaction badges */}
      {hasReactions && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          marginTop: '4px',
          justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
        }}>
          {Object.entries(msgReactions).map(([emoji, count]) => (
            <button
              key={emoji}
              onClick={() => onReact(msgKey, emoji)}
              style={{
                background: 'rgba(82,136,193,0.18)',
                border: '1px solid rgba(82,136,193,0.35)',
                borderRadius: '12px', padding: '3px 9px',
                cursor: 'pointer', fontSize: '14px', color: '#a8c8e8',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}
            >
              {emoji} <span style={{ fontWeight: 700, fontSize: '12px' }}>{count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Contacts Modal ───────────────────────────────────────────────────────────

export const ContactsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const contactItems = [
    { icon: Send, label: 'Telegram', value: '@extremum7', href: 'https://t.me/extremum7' },
    { icon: Github, label: 'GitHub', value: 'github.com/extremum7', href: 'https://github.com/extremum7' },
    { icon: Mail, label: 'Email', value: 'alex@botdev.pro', href: 'mailto:alex@botdev.pro' },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#17212b',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '360px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          animation: 'modalSlideIn 0.2s ease-out',
        }}
      >
        {/* Profile header */}
        <div style={{
          background: 'linear-gradient(160deg, #2b5278 0%, #1a3550 100%)',
          padding: '28px 24px 24px',
          position: 'relative',
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '14px', right: '14px',
              background: 'rgba(255,255,255,0.12)',
              border: 'none', cursor: 'pointer',
              borderRadius: '50%', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
          >
            <X size={16} color="#fff" />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: '#5288c1',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '26px', fontWeight: 700,
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            }}>
              АП
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '20px' }}>Александр</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginTop: '4px' }}>Telegram Bot Developer</div>
            </div>
          </div>
        </div>

        {/* Contact list */}
        <div style={{ padding: '8px 0' }}>
          {contactItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <a
                key={i}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center',
                  padding: '12px 24px', gap: '16px',
                  textDecoration: 'none', cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: '42px', height: '42px', borderRadius: '50%',
                  background: 'rgba(82,136,193,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={18} color="#5288c1" />
                </div>
                <div>
                  <div style={{ color: '#fff', fontSize: '15px', fontWeight: 500 }}>{item.value}</div>
                  <div style={{ color: '#708499', fontSize: '12px', marginTop: '2px' }}>{item.label}</div>
                </div>
              </a>
            );
          })}
        </div>

        {/* CTA button */}
        <div style={{ padding: '8px 16px 20px' }}>
          <a
            href="https://t.me/extremum7"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', background: '#5288c1', color: '#fff',
              borderRadius: '12px', padding: '13px',
              textDecoration: 'none', fontWeight: 600, fontSize: '15px',
            }}
          >
            <Send size={16} color="#fff" style={{ transform: 'translateX(1px)' }} />
            Написать в Telegram
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── Other Components ──────────────────────────────────────────────────────────────

const TypingIndicator = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
    <div style={{
      background: '#182533', borderRadius: '18px 18px 18px 4px',
      padding: '14px 18px', display: 'flex', gap: '5px', alignItems: 'center',
    }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: '#708499', display: 'block',
          animation: 'tgTypingBounce 1.3s infinite ease-in-out',
          animationDelay: `${i * 0.18}s`,
        }} />
      ))}
    </div>
  </div>
);

const EmptyState = () => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0e1621', color: '#708499', gap: '16px' }}>
    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(82,136,193,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Send size={36} color="#5288c1" />
    </div>
    <div style={{ textAlign: 'center' }}>
      <p style={{ color: '#ffffff', fontSize: '18px', fontWeight: 600, margin: '0 0 6px' }}>Выберите проект</p>
      <p style={{ color: '#708499', fontSize: '14px', margin: 0 }}>Нажмите на проект слева</p>
    </div>
  </div>
);

const HeaderBtn = ({ icon: Icon, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
        border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s ease',
      }}
    >
      <Icon size={20} color="#708499" />
    </button>
  );
};

// ─── Main ChatView ──────────────────────────────────────────────────────────────

const ChatView = ({
  project, headerProject, isTyping,
  onBack, isMobile,
  reactions, onReact,
  contactsOpen, setContactsOpen,
}) => {
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll to TOP when switching to a new project
  useEffect(() => {
    if (!isTyping && messagesContainerRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) messagesContainerRef.current.scrollTop = 0;
      }, 80);
    }
  }, [project, isTyping]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text) return;
    window.open(`https://t.me/extremum7?text=${encodeURIComponent(text)}`, '_blank');
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!project && !headerProject) return <EmptyState />;

  const displayProject = project || headerProject;
  const hp = headerProject || displayProject;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0e1621', overflow: 'hidden', minWidth: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '0 8px',
        height: '56px',
        background: '#17212b',
        borderBottom: '1px solid #0d1117',
        flexShrink: 0,
      }}>
        {/* Mobile back button */}
        {isMobile && (
          <button
            onClick={onBack}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '8px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: '2px',
            }}
          >
            <ChevronLeft size={26} color="#5288c1" />
          </button>
        )}

        {/* Avatar */}
        <div style={{ position: 'relative', flexShrink: 0, marginLeft: isMobile ? '0' : '8px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: hp.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: '14px',
          }}>
            {hp.initials}
          </div>
          {hp.isOnline && (
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: '11px', height: '11px', borderRadius: '50%',
              background: '#4dcd5e', border: '2px solid #17212b',
            }} />
          )}
        </div>

        {/* Name & status */}
        <div style={{ flex: 1, marginLeft: '12px', minWidth: 0 }}>
          <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '16px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getBotName(hp)}
          </div>
          <div style={{ fontSize: '13px', marginTop: '1px', color: isTyping ? '#4dcd5e' : '#708499', transition: 'color 0.2s' }}>
            {isTyping ? 'печатает...' : (hp.memberCount || hp.username)}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <HeaderBtn icon={Search} />
          <HeaderBtn icon={Phone} onClick={() => setContactsOpen(true)} />
          <HeaderBtn icon={MoreVertical} />
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column' }}
        className="tg-scrollbar"
      >
        {isTyping ? (
          <>
            <div style={{ flex: 1 }} />
            <TypingIndicator />
          </>
        ) : (
          <>
            {displayProject.messages.map(msg => (
              <ReactionWrapper
                key={msg.id}
                msg={msg}
                projectId={String(displayProject.id)}
                reactions={reactions}
                onReact={onReact}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Launch Button — only shown when project has a demo link */}
      {!isTyping && displayProject.link && (
        <div style={{
          padding: '8px 16px',
          background: '#17212b',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          flexShrink: 0,
        }}>
          <a
            href={displayProject.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderRadius: '12px',
              background: 'transparent',
              border: '1.5px solid #5288c1',
              color: '#5288c1',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '14px',
              transition: 'background 0.15s, color 0.15s',
              letterSpacing: '0.2px',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#5288c1';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#5288c1';
            }}
          >
            <ExternalLink size={15} />
            Запустить демо-бота
          </a>
        </div>
      )}

      {/* Input Bar */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '8px 12px',
        background: '#17212b',
        borderTop: '1px solid #0d1117',
        gap: '8px', flexShrink: 0, height: '60px',
      }}>
        <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paperclip size={22} color="#708499" />
        </button>
        <input
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Написать..."
          style={{
            flex: 1,
            background: '#212d3b',
            borderRadius: '22px',
            padding: '10px 16px',
            border: 'none', outline: 'none',
            color: '#ffffff', fontSize: '15px',
            caretColor: '#5288c1',
          }}
        />
        <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Smile size={22} color="#708499" />
        </button>
        <button
          onClick={handleSend}
          style={{
            background: inputText.trim() ? '#5288c1' : '#2b5278',
            border: 'none', cursor: inputText.trim() ? 'pointer' : 'default',
            padding: '10px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s ease',
          }}
        >
          <Send size={18} color="#ffffff" style={{ transform: 'translateX(1px)' }} />
        </button>
      </div>
    </div>
  );
};

export default ChatView;
